import { useState } from 'react'
import { Button, SimpleGrid, Text } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FiChevronRight } from 'react-icons/fi'
import { explainCopy } from '../../lib/explainCopy'
import type { UnionFootprint } from '../../types/geoAnalytics'
import ProfileCard from './ProfileCard'
import { UnionFootprintDetailModal } from './UnionFootprintDetailModal'
import { StatTile } from '../shared/viz'

interface UnionFootprintCardProps {
  union: UnionFootprint
  geoUnitId: string
  unitName: string
}

const numberFmt = new Intl.NumberFormat('en-US')

/** Your organization's on-the-ground footprint inside this geography (live committee aggregation). */
export function UnionFootprintCard({ union, geoUnitId, unitName }: UnionFootprintCardProps) {
  const [isOpen, setIsOpen] = useState(false)

  const members = union.membersSuppressed
    ? 'Insufficient data'
    : union.members != null
      ? numberFmt.format(union.members)
      : '—'
  const leverage = union.leverageIndexPct != null ? `${union.leverageIndexPct.toFixed(1)}%` : '—'

  return (
    <ProfileCard
      eyebrow="Union footprint"
      title={`On the ground in ${unitName}`}
      source="Committee data (live aggregation) · counts under 10 suppressed"
    >
      <SimpleGrid columns={{ base: 2, md: 4 }} gap={4} mt={3}>
        <StatTile label="Members living in district" value={members} />
        <StatTile label="Worksites" value={numberFmt.format(union.worksites)} />
        <StatTile label="Stewards" value={numberFmt.format(union.stewards)} />
        <StatTile
          label="Leverage index"
          value={
            <Tooltip content={explainCopy.leverageIndex()} showArrow>
              <Text as="span" borderBottom="1px dashed" borderColor="accent" cursor="help" display="inline-block">
                {leverage}
              </Text>
            </Tooltip>
          }
        />
      </SimpleGrid>

      {union.worksites > 0 && (
        <Button
          variant='link'
          colorPalette="brand"
          size="sm"
          mt={4}
          onClick={() => setIsOpen(true)}>View worksites
                  <FiChevronRight /></Button>
      )}

      <UnionFootprintDetailModal
        geoUnitId={geoUnitId}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </ProfileCard>
  );
}
